/**
 * Pacarion Home History Report, Phase 1 (snapshot, labels, share link).
 * CommonJS only. No object spread: the iOS bundle cannot load Babel's spread helper.
 * Phase 2 buyer transfer is not built here. A frozen snapshot is what a later
 * copy-on-claim can read. Phase 1 has no maintenance percentage.
 */

const { PACARION_BRAND_COLOR, PACARION_TAGLINE, PACARION_WORDMARK } = require('./sponsorlessBrand');
const { formatPhoneDisplay } = require('./phoneFormat');
const { stripContacts, stripDueTiming } = require('./homeTransfer');
const { constructionLabel, exteriorLabel, foundationLabel, heaterSummary, roofMaterialLabel } = require('./homeProfile');
const {
  LABEL_OWNER,
  LABEL_ATTACHED,
  LABEL_ON_FILE,
  LABEL_LEGEND,
  reportFooterFull,
  installedFact,
} = require('./reportLegalCopy');

const PRODUCT_NAME = 'Pacarion Home History Report';
const REPORT_TAGLINE = PACARION_TAGLINE;
const TEXT_VERSION = '2026-10-21.home_report_publish.v1';
const CONSENT_TYPE = 'home_report_publish';
const SCHEMA_VERSION = 1;

const COVER_LINE = "Owner's record, not a disclosure or inspection. The owner of this home created this report from their own Pacarion records and chose what to include. It is not a seller's disclosure, a flood disclosure, a home inspection, an appraisal, or a warranty, and Pacarion did not check it. Get your own inspection and review the seller's disclosures.";

const EMPTY_SECTION = "No entries in this section. This doesn't mean nothing happened.";
const TOGGLE_HELP = "Turn off any section you don't want to share.";
const MAINTENANCE_HELP = 'Maintenance history includes every completed reminder. You can turn the whole section off. You can\'t remove individual entries.';
const DOCUMENT_HELP = 'Check each file before you share it. Receipts can show payment details.';
const INSURANCE_FORM_WARNING = 'Wind mitigation and 4-point forms can include a policy number and the owner\'s name. They stay off unless you check them. Read the file before you share it.';
const FLOOD_NOTE_WARNING = 'Flood history belongs on your Florida flood disclosure form, not in this report. Talk to your sponsor.';
const CLAIM_NOTE = 'Buying this home? You can claim its history free after closing.';
const DOWNLOAD_NOTE = 'A downloaded copy cannot be revoked.';
const VIEWER_BANNER_LEAD = 'Owner-reported unless a document is on file.';

const SHARE_DAY_CHOICES = [7, 30, 90];
const DEFAULT_SHARE_DAYS = 30;
const REPORT_TERMS_PATH = '/report-terms';

const INTERSTITIAL_TITLE = 'Before you read';
const INTERSTITIAL_BODY = "This is the owner's own record of how they cared for this home. It isn't a seller's disclosure or an inspection, Pacarion didn't check it, and the owner chose what to include.";
const INTERSTITIAL_BUTTON = 'View report';
const INTERSTITIAL_TERMS = 'By viewing, you agree to the Report Viewer Terms.';

const SHARE_CONSENT_BODY = "This report is your own record. It is not your seller's disclosure or flood disclosure, and it doesn't replace them. If you know about a problem with the home, disclose it on those forms and talk to your sponsor or a real estate attorney. Hiding sections doesn't change what you're required to disclose.";
const SHARE_CONSENT_CHECKBOX = "I've reviewed this report. It's accurate to the best of my knowledge, it's my own record (not a seller's disclosure or inspection), and I have the right to share each document and photo I included. I understand the report will list any sections I left out, that anyone with the link can view and download it until it expires or I revoke it, and that downloaded copies can't be revoked.";
const CREATE_BUTTON = 'Create report';

const FLORIDA_CAPTION = "Shown for reference only. These are dates of documents the owner keeps. Pacarion doesn't review the forms. Your insurer decides discounts, eligibility, and whether a form is still accepted.";
const WIND_NOTE = "Florida generally accepts these forms for up to 5 years if the home hasn't materially changed. Ask your insurer.";

const VIEWER_TERMS = "Report Viewer Terms. You're viewing a Home History Report that a homeowner created and shared using Pacarion, a service of Pacarion LLC (Florida). The homeowner authored the report and chose its contents. Pacarion didn't inspect the home or check the report. The report is not a seller's disclosure, inspection, appraisal, or warranty. It's provided as is, and to the extent permitted by law Pacarion disclaims warranties about it and is not liable for decisions you make based on it. Nothing here limits non-waivable consumer-protection rights. Don't copy or redistribute documents or photos in the report except to evaluate the home. Florida law governs. Questions or concerns about a report (including a report you think is misleading or that contains your information): support@pacarion.com · PO Box 530213, DeBary, FL 32753.";

const SECTIONS = [
  { id: 'systems', name: 'Major systems', defaultOn: true },
  { id: 'maintenance', name: 'Maintenance history', defaultOn: true },
  { id: 'warranties', name: 'Warranties', defaultOn: true },
  { id: 'florida', name: 'Florida insurance items', defaultOn: true, floridaOnly: true },
  { id: 'documents', name: 'Documents', defaultOn: false },
  { id: 'photos', name: 'Photos', defaultOn: false },
];

const STATE_LINES = {
  FL: 'It is not the Florida flood disclosure (s. 689.302, Fla. Stat.).',
  TX: "It is not the Seller's Disclosure Notice (Tex. Prop. Code s. 5.008).",
  NY: 'It is not the Property Condition Disclosure Statement (N.Y.).',
  LA: 'It is not the Louisiana Property Disclosure Document.',
  TN: 'It is not the Tennessee Residential Property Disclosure.',
  MN: 'It is not a qualified third-party inspection report under Minnesota law.',
};

const PROPERTY_TYPES = {
  single_family: 'Single-family',
  condo: 'Condo',
  townhome: 'Townhome',
  mobile_home: 'Mobile home',
};

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function clean(value) {
  return String(value == null ? '' : value).replace(/\s+/g, ' ').trim();
}

function stateCode(value) {
  const raw = clean(value).toUpperCase();
  if (raw === 'FLORIDA') return 'FL';
  if (raw === 'TEXAS') return 'TX';
  if (raw === 'NEW YORK') return 'NY';
  if (raw === 'LOUISIANA') return 'LA';
  if (raw === 'TENNESSEE') return 'TN';
  if (raw === 'MINNESOTA') return 'MN';
  if (/^[A-Z]{2}$/.test(raw)) return raw;
  return '';
}

function floridaHome(client) {
  return stateCode(client && client.state) === 'FL';
}

function dateParts(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value == null ? '' : value));
  if (!match) return null;
  const month = Number(match[2]);
  const day = Number(match[3]);
  const year = Number(match[1]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return { year: year, month: month, day: day };
}

function formatLongDate(value) {
  const parts = dateParts(value);
  if (!parts) return '';
  return MONTHS[parts.month - 1] + ' ' + parts.day + ', ' + parts.year;
}

function formatMonthYear(value) {
  const parts = dateParts(value);
  if (!parts) return '';
  return MONTHS[parts.month - 1].slice(0, 3) + ' ' + parts.year;
}

function yearNumber(value) {
  if (Array.isArray(value)) return null;
  const match = /^(\d{4})$/.exec(clean(value));
  if (!match) return null;
  const year = Number(match[1]);
  if (year < 1800 || year > 2100) return null;
  return year;
}

function labeled(map, value) {
  const key = clean(value);
  if (!key) return '';
  if (map && Object.prototype.hasOwnProperty.call(map, key)) return map[key];
  return key.replace(/_/g, ' ');
}

function featureOn(client, id) {
  const features = client && client.features;
  if (!Array.isArray(features)) return false;
  for (let index = 0; index < features.length; index += 1) {
    if (String(features[index]) === id) return true;
  }
  return false;
}

function idList(value) {
  const out = [];
  const seen = {};
  const list = Array.isArray(value) ? value : [];
  list.forEach((item) => {
    const id = clean(item);
    if (!id || seen[id]) return;
    seen[id] = true;
    out.push(id);
  });
  return out;
}

function linkList(value) {
  const out = [];
  const seen = {};
  const list = Array.isArray(value) ? value : [];
  list.forEach((item) => {
    if (!item) return;
    const documentId = clean(item.documentId);
    const systemId = clean(item.systemId);
    if (!documentId || !systemId) return;
    const key = documentId + '|' + systemId;
    if (seen[key]) return;
    seen[key] = true;
    out.push({ documentId: documentId, systemId: systemId });
  });
  return out;
}

function availableSections(florida) {
  return SECTIONS.filter((section) => !section.floridaOnly || florida);
}

function normalizeToggles(raw, options) {
  const source = raw && typeof raw === 'object' ? raw : {};
  const sectionsIn = source.sections && typeof source.sections === 'object' ? source.sections : {};
  const florida = Boolean(options && options.florida);
  const sections = {};
  SECTIONS.forEach((section) => {
    if (section.floridaOnly && !florida) {
      sections[section.id] = false;
      return;
    }
    if (Object.prototype.hasOwnProperty.call(sectionsIn, section.id)) {
      sections[section.id] = sectionsIn[section.id] === true;
    } else {
      sections[section.id] = section.defaultOn === true;
    }
  });
  return {
    sections: sections,
    ownerName: source.ownerName === true,
    streetAddress: source.streetAddress === true,
    taskNotes: source.taskNotes === true,
    documentIds: idList(source.documentIds),
    photoIds: idList(source.photoIds),
    links: linkList(source.links),
  };
}

function defaultToggles(florida) {
  return normalizeToggles({}, { florida: florida });
}

function sectionOn(toggles, id) {
  return Boolean(toggles && toggles.sections && toggles.sections[id]);
}

function omission(toggles, florida) {
  const omitted = [];
  availableSections(florida).forEach((section) => {
    if (!sectionOn(toggles, section.id)) omitted.push(section.name);
  });
  const privacy = [];
  if (!toggles.ownerName) privacy.push('owner name');
  if (!toggles.streetAddress) privacy.push('street address');
  if (sectionOn(toggles, 'maintenance') && !toggles.taskNotes) privacy.push('task notes');
  const statement = omitted.length
    ? 'Sections not included by the owner: ' + omitted.join(', ') + '.'
    : 'The owner included all available sections.';
  const privacyLine = privacy.length ? 'Hidden for privacy: ' + privacy.join(', ') + '.' : '';
  const choseFiles = sectionOn(toggles, 'documents') || sectionOn(toggles, 'photos');
  return {
    omitted: omitted,
    privacy: privacy,
    statement: statement,
    privacyLine: privacyLine,
    documentChoice: choseFiles ? 'The owner chose which documents and photos to include.' : '',
  };
}

function formatReportNumber(version) {
  const value = Math.floor(Number(version));
  const safe = value > 0 ? value : 1;
  let padded = String(safe);
  while (padded.length < 4) padded = '0' + padded;
  return 'HR-' + padded;
}

function shareDays(value) {
  const days = Math.floor(Number(value));
  if (SHARE_DAY_CHOICES.indexOf(days) === -1) return DEFAULT_SHARE_DAYS;
  return days;
}

function shareIsOpen(share, now) {
  if (!share || share.revoked_at) return false;
  const expires = new Date(share.expires_at).getTime();
  const at = now instanceof Date ? now.getTime() : new Date(now).getTime();
  if (Number.isNaN(expires) || Number.isNaN(at)) return false;
  return expires > at;
}

function reportShareFromPath(pathname) {
  const path = String(pathname || '').split('?')[0].split('#')[0].replace(/\/+$/, '');
  const match = /^\/report\/([A-Za-z0-9_-]{22,128})$/.exec(path);
  if (!match) return null;
  return { token: match[1] };
}

function isReportTermsPath(pathname) {
  const path = String(pathname || '').split('?')[0].split('#')[0].replace(/\/+$/, '');
  return path === REPORT_TERMS_PATH;
}

function reportPath(token) {
  return '/report/' + token;
}

function addressLabel(client, showStreet) {
  const city = clean(client && client.city);
  const state = stateCode(client && client.state) || clean(client && client.state);
  const zip = clean(client && client.zip_code);
  const placeCore = [city, state].filter(Boolean).join(', ');
  const place = [placeCore, zip].filter(Boolean).join(' ');
  const street = clean(client && client.address);
  if (showStreet && street) {
    return place ? street + ', ' + place : street;
  }
  return place;
}

function ownerDisplayName(client) {
  return [clean(client && client.first_name), clean(client && client.last_name)].filter(Boolean).join(' ');
}

function requestMessage(agentName) {
  const name = clean(agentName) || 'Your sponsor';
  return name + ' asked for your Home History Report for your listing.';
}

function shareConsentNotice() {
  return 'Before you share. ' + SHARE_CONSENT_BODY + ' ' + SHARE_CONSENT_CHECKBOX + ' Terms. Privacy Policy.';
}

function formDated(doc) {
  const raw = doc && doc.purchase_date;
  if (!raw) return '';
  return formatMonthYear(raw) || formatLongDate(raw);
}

function requestCardOpen(request) {
  if (!request || request.status !== 'requested') return false;
  if (request.dismissedAt || request.dismissed_at) return false;
  return true;
}

function reportRequestDecision(row, action, now) {
  if (action !== 'decline' && action !== 'dismiss') {
    return { ok: false, status: 400, error: 'Choose decline or dismiss.', notify: false };
  }
  if (!row) return { ok: false, status: 404, error: 'Request not found.', notify: false };
  if (row.status === 'declined' && action === 'decline') {
    return { ok: true, status: 200, patch: null, notify: false };
  }
  if (action === 'dismiss' && row.status === 'requested' && (row.dismissed_at || row.dismissedAt)) {
    return { ok: true, status: 200, patch: null, notify: false };
  }
  if (row.status !== 'requested') {
    return { ok: false, status: 409, error: 'This request is no longer open.', notify: false };
  }
  if (action === 'decline') {
    return { ok: true, status: 200, patch: { status: 'declined' }, notify: false };
  }
  const when = now instanceof Date ? now : new Date(now || Date.now());
  return {
    ok: true,
    status: 200,
    patch: { dismissed_at: when.toISOString() },
    notify: false,
  };
}

function agentReportStatus(request, share, now) {
  if (!request || !request.status) return 'none';
  if (request.status === 'declined') return 'Declined';
  if (request.status === 'requested') return 'Requested';
  if (request.status !== 'shared') return 'none';
  if (!share) return 'Expired';
  if (share.revoked_at || share.revokedAt) return 'Revoked';
  const open = shareIsOpen({
    expires_at: share.expires_at || share.expiresAt,
    revoked_at: null,
  }, now || new Date());
  return open ? 'Shared' : 'Expired';
}

function requestCanTakeShare(request, share) {
  if (!request || !request.status) return false;
  if (request.status === 'requested') return true;
  if (request.status !== 'shared') return false;
  return Boolean(share && (share.revoked_at || share.revokedAt));
}

function validShareId(value) {
  const shareId = String(value || '').trim();
  if (!/^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(shareId)) {
    return '';
  }
  return shareId;
}

function preparedByLine(name, place, showStreet) {
  const who = name || 'the homeowner';
  if (showStreet && place) {
    if (name) return 'Prepared by ' + name + ', the owner of ' + place + '.';
    return 'Prepared by the homeowner of ' + place + '.';
  }
  if (place) return 'Prepared by ' + who + '. Location: ' + place + '.';
  return 'Prepared by ' + who + '.';
}

function docType(category) {
  const cat = clean(category).toLowerCase();
  if (cat.indexOf('invoice') !== -1) return 'Invoice';
  if (cat.indexOf('permit') !== -1) return 'Permit';
  if (cat.indexOf('wind') !== -1) return 'Wind mitigation form';
  if (cat.indexOf('4-point') !== -1 || cat.indexOf('4 point') !== -1 || cat.indexOf('four-point') !== -1) {
    return '4-point inspection';
  }
  if (cat.indexOf('roof inspection') !== -1) return 'Roof inspection';
  if (cat.indexOf('inspection') !== -1) return 'Inspection report';
  if (cat.indexOf('warrant') !== -1) return 'Warranty';
  if (cat.indexOf('receipt') !== -1) return 'Receipt';
  if (cat.indexOf('manual') !== -1) return 'Manual';
  if (cat.indexOf('service') !== -1) return 'Service record';
  return 'Document';
}

function docKind(doc) {
  const cat = clean(doc && doc.category).toLowerCase();
  const title = clean(doc && (doc.title || doc.file_name)).toLowerCase();
  const blob = cat + ' ' + title;
  if (blob.indexOf('wind') !== -1 && blob.indexOf('mitig') !== -1) return 'wind';
  if (cat.indexOf('wind') !== -1) return 'wind';
  if (blob.indexOf('4-point') !== -1 || blob.indexOf('4 point') !== -1 || blob.indexOf('four-point') !== -1) {
    return 'four';
  }
  if (blob.indexOf('roof inspection') !== -1) return 'roof-inspection';
  return '';
}

function docDate(doc) {
  return (doc && (doc.purchase_date || doc.warranty_expiry || doc.created_at)) || '';
}

function documentIncluded(toggles, id) {
  if (!sectionOn(toggles, 'documents')) return false;
  return toggles.documentIds.indexOf(id) !== -1;
}

function linkedDocs(documents, toggles, systemId) {
  const ids = {};
  (toggles.links || []).forEach((link) => {
    if (link.systemId === systemId) ids[link.documentId] = true;
  });
  return (documents || []).filter((doc) => doc && doc.id && ids[doc.id]);
}

function sourceLabel(documents, toggles, systemId) {
  const linked = linkedDocs(documents, toggles, systemId);
  if (!linked.length) return LABEL_OWNER;
  for (let index = 0; index < linked.length; index += 1) {
    if (documentIncluded(toggles, linked[index].id)) return LABEL_ATTACHED;
  }
  return LABEL_ON_FILE;
}

function sourceDetail(documents, toggles, systemId) {
  const linked = linkedDocs(documents, toggles, systemId);
  if (!linked.length) return '';
  let chosen = linked[0];
  for (let index = 0; index < linked.length; index += 1) {
    if (documentIncluded(toggles, linked[index].id)) {
      chosen = linked[index];
      break;
    }
  }
  const dated = formatLongDate(docDate(chosen));
  const type = docType(chosen.category);
  return dated ? type + ' dated ' + dated : type;
}

function systemData(row) {
  if (!row || !row.system_data || typeof row.system_data !== 'object') return {};
  return row.system_data;
}

function pushSystem(list, item) {
  if (!item || !item.name) return;
  const hasFact = item.installed || item.detail || item.make || item.model;
  if (!hasFact) return;
  list.push(item);
}

function systemsFromHome(client, systemRows, nowYear) {
  const byType = {};
  (systemRows || []).forEach((row) => {
    if (row && row.system_type) byType[row.system_type] = systemData(row);
  });
  const list = [];
  const roofData = byType.roof || {};
  const roofYear = yearNumber(roofData.install_year) || yearNumber(client.roof_install_year) || yearNumber(client.roof_year);
  const roofDetail = roofMaterialLabel(roofData.material || roofData.roof_material || client.roof_material)
    || clean(roofData.type || client.roof_type);
  pushSystem(list, {
    id: 'roof',
    name: 'Roof',
    installed: roofYear,
    detail: roofDetail,
    make: clean(roofData.manufacturer),
    model: clean(roofData.model_number),
  });

  const hvacData = byType.hvac || {};
  const hvacYears = [];
  const fromColumns = Array.isArray(client.hvac_install_years) ? client.hvac_install_years : [];
  fromColumns.forEach((year) => {
    const parsed = yearNumber(year);
    if (parsed) hvacYears.push(parsed);
  });
  const hvacDataYear = yearNumber(hvacData.install_year);
  if (!hvacYears.length && hvacDataYear) hvacYears.push(hvacDataYear);
  if (!hvacYears.length && (clean(hvacData.manufacturer) || clean(hvacData.model_number))) {
    hvacYears.push(null);
  }
  hvacYears.forEach((year, index) => {
    pushSystem(list, {
      id: hvacYears.length > 1 ? 'hvac-' + (index + 1) : 'hvac',
      name: hvacYears.length > 1 ? 'HVAC unit ' + (index + 1) : 'HVAC',
      installed: year,
      detail: '',
      make: index === 0 ? clean(hvacData.manufacturer) : '',
      model: index === 0 ? clean(hvacData.model_number) : '',
    });
  });

  const water = byType.water_heater || {};
  pushSystem(list, {
    id: 'water_heater',
    name: 'Water heater',
    installed: yearNumber(water.install_year) || yearNumber(client.water_heater_install_year),
    detail: clean(water.type) || heaterSummary(client.water_heater_type, client.water_heater_install_year),
    make: clean(water.manufacturer),
    model: clean(water.model_number),
  });

  const pool = byType.pool_equipment || {};
  const pumpYear = yearNumber(pool.pump_year) || yearNumber(client.pool_pump_install_year);
  const heaterYear = yearNumber(pool.heater_year) || yearNumber(client.pool_heater_install_year);
  pushSystem(list, {
    id: 'pool_pump',
    name: 'Pool pump',
    installed: pumpYear,
    detail: '',
    make: '',
    model: clean(pool.pump_model),
  });
  pushSystem(list, {
    id: 'pool_heater',
    name: 'Pool heater',
    installed: heaterYear,
    detail: '',
    make: '',
    model: clean(pool.heater_model),
  });

  const well = byType.well_pump || {};
  pushSystem(list, {
    id: 'well_pump',
    name: 'Well pump',
    installed: yearNumber(well.install_year) || yearNumber(client.well_pump_install_year),
    detail: '',
    make: clean(well.manufacturer),
    model: clean(well.model_number),
  });

  const septic = byType.septic_system || {};
  pushSystem(list, {
    id: 'septic',
    name: 'Septic',
    installed: yearNumber(septic.install_year) || yearNumber(client.septic_install_year),
    detail: clean(septic.type),
    make: '',
    model: '',
  });

  const extras = [
    ['electrical_panel', 'Electrical panel'],
    ['plumbing', 'Plumbing'],
    ['water_filter', 'Water filter'],
    ['generator', 'Generator'],
    ['solar_panels', 'Solar'],
  ];
  extras.forEach((pair) => {
    const data = byType[pair[0]];
    if (!data) return;
    pushSystem(list, {
      id: pair[0],
      name: pair[1],
      installed: yearNumber(data.install_year),
      detail: clean(data.type),
      make: clean(data.manufacturer),
      model: clean(data.model_number),
    });
  });

  return list.map((item) => {
    const bits = [];
    if (item.detail) bits.push(item.detail);
    const makeModel = [item.make, item.model].filter(Boolean).join(' ');
    if (makeModel) bits.push(makeModel);
    if (item.installed) bits.push(installedFact(item.installed, nowYear));
    return {
      id: item.id,
      title: item.name,
      detail: bits.join(' · '),
      installed: item.installed || null,
    };
  });
}

function nestedTask(task) {
  if (!task) return null;
  return task.task || task.tasks || null;
}

function nestedSeries(task) {
  if (!task) return null;
  return task.series || task.client_task_series || null;
}

function taskTitle(task) {
  const direct = clean(task && task.task_name);
  if (direct) return direct;
  const library = nestedTask(task);
  const fromLibrary = library && clean(library.title);
  if (fromLibrary) return fromLibrary;
  const series = nestedSeries(task);
  const fromSeries = series && clean(series.title);
  if (fromSeries) return fromSeries;
  return clean(task && task.series_title) || 'Maintenance';
}

function taskCategory(task) {
  const direct = clean(task && task.category);
  if (direct) return direct;
  const library = nestedTask(task);
  return clean(library && library.category);
}

function taskType(task) {
  const direct = clean(task && task.type);
  if (direct) return direct;
  const library = nestedTask(task);
  return clean(library && library.type);
}

function labelKey(value) {
  return String(value == null ? '' : value).trim().toLowerCase().replace(/\s+/g, ' ');
}

const FINANCIAL_CATEGORIES = [
  'Financial & Administrative',
  'Insurance',
  'Mortgage',
  'Property Tax',
  'Taxes',
  'Administrative',
];

const FINANCIAL_DEADLINE_TITLES = [
  'homestead exemption reminder',
  'homestead filing deadline',
  'trim notice review',
  'property tax early-pay discount',
  'property tax discount last chance',
  'property tax final due',
];

function financialCategoryName(name) {
  const key = labelKey(name);
  if (!key || key === 'home') return false;
  for (let index = 0; index < FINANCIAL_CATEGORIES.length; index += 1) {
    if (labelKey(FINANCIAL_CATEGORIES[index]) === key) return true;
  }
  return false;
}

function financialDeadlineTitle(title) {
  return FINANCIAL_DEADLINE_TITLES.indexOf(labelKey(title)) !== -1;
}

function financialDeadline(task) {
  if (labelKey(taskType(task)) === 'financial') return true;
  if (financialCategoryName(taskCategory(task))) return true;
  if (financialDeadlineTitle(taskTitle(task))) return true;
  const series = nestedSeries(task);
  return financialDeadlineTitle(series && series.title);
}

function reportKeepsDueTiming(item) {
  if (!item) return false;
  if (item.deadline === true) return true;
  if (labelKey(item.type) === 'financial') return true;
  if (financialCategoryName(item.group) || financialCategoryName(item.category)) return true;
  return financialDeadlineTitle(item.title);
}

function daysFromDue(due, completed) {
  const left = dateParts(due);
  const right = dateParts(completed);
  if (!left || !right) return null;
  const start = Date.UTC(left.year, left.month - 1, left.day);
  const end = Date.UTC(right.year, right.month - 1, right.day);
  return Math.round((end - start) / 86400000);
}

function timingNote(task) {
  if (!financialDeadline(task)) return '';
  const completed = task && (task.completed_at || task.due_date);
  const gap = daysFromDue(task && task.due_date, completed);
  if (gap == null) return '';
  if (gap > 0) return gap + (gap === 1 ? ' day after the due date' : ' days after the due date');
  if (gap < 0) return 'Before the due date';
  return 'On time';
}

function maintenanceDetail(task) {
  const category = taskCategory(task);
  const when = formatLongDate(task && (task.completed_at || task.due_date));
  const timing = timingNote(task);
  const bits = [];
  if (category) bits.push(category);
  if (when) bits.push(when);
  if (timing) bits.push(timing);
  return bits.join(' · ');
}

function maintenanceItems(tasks, toggles) {
  const rows = (Array.isArray(tasks) ? tasks : []).filter((task) => task && task.completed === true);
  rows.sort((left, right) => {
    const l = String((left && (left.completed_at || left.due_date)) || '');
    const r = String((right && (right.completed_at || right.due_date)) || '');
    if (l === r) return 0;
    return l < r ? 1 : -1;
  });
  return rows.map((task) => {
    const category = taskCategory(task);
    return {
      id: task.id || '',
      title: taskTitle(task),
      detail: maintenanceDetail(task),
      label: LABEL_OWNER,
      note: toggles.taskNotes ? presentTaskNote(taskNoteSource(task)) : '',
      group: category || 'Home',
      when: formatLongDate(task && (task.completed_at || task.due_date)),
      deadline: financialDeadline(task),
    };
  });
}

function taskNoteSource(task) {
  const homeowner = clean(task && task.client_notes);
  const record = clean(task && task.notes);
  if (homeowner && record && homeowner !== record) return homeowner + ' ' + record;
  return homeowner || record;
}

function presentTaskNote(value) {
  return stripContacts(value).replace(/ +,/g, ',').replace(/,{2,}/g, ',').replace(/ +/g, ' ').trim();
}

function notesMentionFlood(tasks) {
  const rows = Array.isArray(tasks) ? tasks : [];
  for (let index = 0; index < rows.length; index += 1) {
    const note = taskNoteSource(rows[index]).toLowerCase();
    if (note.indexOf('flood') !== -1) return true;
  }
  return false;
}

function warrantyItems(client, documents, systemRows, toggles) {
  const items = [];
  (documents || []).forEach((doc) => {
    if (!doc) return;
    const type = docType(doc.category);
    const expiry = doc.warranty_expiry;
    const isWarranty = type === 'Warranty' || Boolean(expiry);
    if (!isWarranty) return;
    const included = documentIncluded(toggles, doc.id);
    const dated = formatLongDate(expiry || docDate(doc));
    items.push({
      id: doc.id,
      title: clean(doc.title || doc.file_name) || 'Warranty',
      detail: dated ? 'Dated ' + dated : '',
      label: included ? LABEL_ATTACHED : LABEL_ON_FILE,
    });
  });
  const renewal = formatMonthYear(client && client.warranty_renewal_date);
  if (renewal) {
    items.push({
      id: 'home-warranty',
      title: 'Home warranty renewal month',
      detail: renewal,
      label: LABEL_OWNER,
    });
  }
  (systemRows || []).forEach((row) => {
    const data = systemData(row);
    const years = Math.floor(Number(data.warranty_years));
    if (!years || years < 1 || years > 100) return;
    items.push({
      id: 'warranty-' + (row.system_type || row.id || items.length),
      title: clean(row.system_type || 'System').replace(/_/g, ' ') + ' warranty term entered',
      detail: years + (years === 1 ? ' year' : ' years'),
      label: LABEL_OWNER,
    });
  });
  return items;
}

function floridaItems(client, documents, toggles, nowYear) {
  const items = [];
  (documents || []).forEach((doc) => {
    const kind = docKind(doc);
    if (!kind) return;
    const included = documentIncluded(toggles, doc.id);
    const dated = formDated(doc);
    let title = 'Inspection';
    let detail = dated ? 'Form dated ' + dated : '';
    if (kind === 'wind') {
      title = 'Wind mitigation inspection (form OIR-B1-1802)';
      detail = dated ? 'Form dated ' + dated : '';
    } else if (kind === 'four') {
      title = '4-point inspection';
      detail = dated ? 'Dated ' + dated : '';
    } else if (kind === 'roof-inspection') {
      title = 'Roof inspection';
      detail = dated ? 'Dated ' + dated : '';
    }
    items.push({
      id: doc.id,
      title: title,
      detail: detail,
      label: included ? LABEL_ATTACHED : LABEL_ON_FILE,
      note: kind === 'wind' ? WIND_NOTE : '',
    });
  });
  const roofYear = yearNumber(client.roof_install_year) || yearNumber(client.roof_year);
  const material = roofMaterialLabel(client.roof_material) || clean(client.roof_type);
  if (roofYear || material) {
    const bits = [];
    if (material) bits.push(material);
    if (roofYear) bits.push(installedFact(roofYear, nowYear));
    items.push({
      id: 'roof-fact',
      title: 'Roof',
      detail: bits.join(' · '),
      label: LABEL_OWNER,
      note: '',
    });
  }
  if (featureOn(client, 'hurricane_shutters')) {
    items.push({
      id: 'shutters',
      title: 'Storm shutters',
      detail: 'Yes',
      label: LABEL_OWNER,
      note: '',
    });
  }
  if (featureOn(client, 'impact_windows')) {
    items.push({
      id: 'impact-windows',
      title: 'Impact windows',
      detail: 'Yes',
      label: LABEL_OWNER,
      note: '',
    });
  }
  return items;
}

function documentItems(documents, toggles) {
  if (!sectionOn(toggles, 'documents')) return [];
  const wanted = {};
  toggles.documentIds.forEach((id) => { wanted[id] = true; });
  return (documents || []).filter((doc) => doc && doc.id && wanted[doc.id]).map((doc) => ({
    id: doc.id,
    title: clean(doc.title || doc.file_name) || 'Document',
    detail: [docType(doc.category), formatLongDate(docDate(doc))].filter(Boolean).join(' · '),
    label: LABEL_ATTACHED,
  }));
}

function photoItems(photos, toggles) {
  if (!sectionOn(toggles, 'photos')) return [];
  const wanted = {};
  toggles.photoIds.forEach((id) => { wanted[id] = true; });
  return (photos || []).filter((photo) => photo && photo.id && wanted[photo.id]).map((photo) => ({
    id: String(photo.id),
    label: clean(photo.label) || 'Photo',
  }));
}

function glanceRows(client) {
  const rows = [];
  const year = yearNumber(client.year_built);
  if (year) rows.push({ label: 'Year built', value: String(year) });
  const feet = Math.floor(Number(client.square_feet));
  if (feet > 0) rows.push({ label: 'Sq ft', value: String(feet) });
  const type = labeled(PROPERTY_TYPES, client.property_type);
  if (type) rows.push({ label: 'Property type', value: type });
  const construction = constructionLabel(client.construction_type);
  if (construction) rows.push({ label: 'Construction', value: construction });
  const exterior = exteriorLabel(client.exterior_finish);
  if (exterior) rows.push({ label: 'Exterior', value: exterior });
  const foundation = foundationLabel(client.foundation_type);
  if (foundation) rows.push({ label: 'Foundation', value: foundation });
  const roof = roofMaterialLabel(client.roof_material);
  if (roof) rows.push({ label: 'Roof', value: roof });
  if (featureOn(client, 'pool')) rows.push({ label: 'Pool', value: 'Yes' });
  return rows;
}

function projectTaskList(project) {
  if (!project) return [];
  const tasks = project.tasks;
  if (Array.isArray(tasks)) return tasks;
  if (typeof tasks !== 'string') return [];
  try {
    const parsed = JSON.parse(tasks);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
}

function vendorTotal(projects) {
  const seen = {};
  let count = 0;
  (projects || []).forEach((project) => {
    projectTaskList(project).forEach((task) => {
      const ids = task && Array.isArray(task.vendor_ids) ? task.vendor_ids : [];
      ids.forEach((id) => {
        const key = clean(id);
        if (!key || seen[key]) return;
        seen[key] = true;
        count += 1;
      });
    });
  });
  return count;
}

function summaryTotals(toggles, counts) {
  const rows = [];
  if (sectionOn(toggles, 'maintenance')) {
    rows.push({ label: 'Completed maintenance', value: String(counts.maintenance || 0) });
  }
  rows.push({ label: 'Projects', value: String(counts.projects || 0) });
  rows.push({ label: 'Vendors used', value: String(counts.vendors || 0) });
  if (sectionOn(toggles, 'documents')) {
    rows.push({ label: 'Documents included', value: String(counts.documents || 0) });
  }
  return rows;
}

function httpsUrl(value) {
  const raw = clean(value);
  if (/^https:\/\/[^\s]+$/i.test(raw)) return raw;
  return '';
}

function sponsorCard(sponsor) {
  if (!sponsor) {
    return {
      kind: 'pacarion',
      wordmark: PACARION_WORDMARK,
      name: '',
      brokerage: '',
      phone: '',
      email: '',
      brandColor: PACARION_BRAND_COLOR,
      line: 'Pacarion LLC provided the software. Pacarion did not inspect the home or check the owner\'s entries or documents.',
    };
  }
  const name = [clean(sponsor.first_name), clean(sponsor.last_name)].filter(Boolean).join(' ');
  const brokerage = clean(sponsor.brokerage);
  const who = [name, brokerage].filter(Boolean).join(', ') || 'The sponsor';
  return {
    kind: 'sponsor',
    wordmark: '',
    name: name,
    brokerage: brokerage,
    teamName: clean(sponsor.team_name),
    phone: formatPhoneDisplay(sponsor.phone),
    email: clean(sponsor.email),
    website: httpsUrl(sponsor.website),
    logoUrl: httpsUrl(sponsor.logo_url),
    headshotUrl: httpsUrl(sponsor.headshot_url),
    brandColor: /^#[0-9a-fA-F]{6}$/.test(clean(sponsor.brand_color)) ? clean(sponsor.brand_color) : PACARION_BRAND_COLOR,
    line: who + ' provides Pacarion to this homeowner. ' + (name || 'The sponsor') + ' did not prepare this report or inspect the home.',
  };
}

function sectionBlock(id, title, items, caption) {
  return {
    id: id,
    title: title,
    caption: caption || '',
    items: items,
    empty: items.length ? '' : EMPTY_SECTION,
  };
}

function aboutParagraphs(snapshot) {
  const leftOut = snapshot.omission;
  const incomplete = 'The report includes only what the owner recorded and chose to share. The owner chose which sections, documents, and photos to include. '
    + leftOut.statement
    + (leftOut.privacyLine ? ' ' + leftOut.privacyLine : '')
    + (leftOut.documentChoice ? ' ' + leftOut.documentChoice : '')
    + " A missing entry does not mean something didn't happen, and an entry does not mean the work was done well.";
  const legend = LABEL_LEGEND;
  const idLine = 'Report #' + snapshot.reportNumber
    + ' · version ' + snapshot.version
    + ' · prepared ' + snapshot.preparedLabel
    + ' · Pacarion LLC, PO Box 530213, DeBary, FL 32753 · support@pacarion.com';
  return [
    'Who made it. The owner of this home prepared this report from records they keep in Pacarion, a home-maintenance app. Pacarion LLC provided the software. Pacarion did not inspect the home or check the owner\'s entries or documents.',
    "What it is not. This report is not a seller's disclosure, a flood disclosure, a home inspection, an appraisal, a survey, a warranty, or an insurance document, and it doesn't replace any of them. It doesn't satisfy any disclosure the seller or a real estate licensee is required to make.",
    'It may be incomplete. ' + incomplete,
    legend,
    "Do your own checking. Hire a licensed home inspector, review the seller's disclosures, check permits with your local building department, and ask the seller or your agent about anything that matters to you.",
    'Sponsor. ' + snapshot.sponsor.line,
    idLine,
  ];
}

function buildReportSnapshot(input) {
  const source = input || {};
  const client = source.client || {};
  const now = source.now instanceof Date ? source.now : new Date(source.now || Date.now());
  const nowYear = now.getUTCFullYear();
  const florida = floridaHome(client);
  const toggles = normalizeToggles(source.toggles, { florida: florida });
  const documents = Array.isArray(source.documents) ? source.documents : [];
  const tasks = Array.isArray(source.tasks) ? source.tasks : [];
  const photos = Array.isArray(source.photos) ? source.photos : [];
  const systemRows = Array.isArray(source.systems) ? source.systems : [];
  const version = Math.floor(Number(source.version)) > 0 ? Math.floor(Number(source.version)) : 1;
  const prepared = source.preparedAt || now.toISOString();
  const showName = toggles.ownerName;
  const name = showName ? ownerDisplayName(client) : '';
  const place = addressLabel(client, toggles.streetAddress);
  const preparedBy = preparedByLine(name, place, toggles.streetAddress);
  const state = stateCode(client.state);
  const systems = sectionOn(toggles, 'systems')
    ? systemsFromHome(client, systemRows, nowYear).map((item) => ({
      id: item.id,
      title: item.title,
      detail: [item.detail, sourceDetail(documents, toggles, item.id)].filter(Boolean).join(' · '),
      label: sourceLabel(documents, toggles, item.id),
      note: '',
    }))
    : [];
  const maintenance = sectionOn(toggles, 'maintenance') ? maintenanceItems(tasks, toggles) : [];
  const warranties = sectionOn(toggles, 'warranties')
    ? warrantyItems(client, documents, systemRows, toggles)
    : [];
  const floridaItemsList = sectionOn(toggles, 'florida')
    ? floridaItems(client, documents, toggles, nowYear)
    : [];
  const includedDocs = documentItems(documents, toggles);
  const includedPhotos = photoItems(photos, toggles);
  const leftOut = omission(toggles, florida);
  const since = formatMonthYear(client.created_at);
  const snapshot = {
    schema: SCHEMA_VERSION,
    productName: PRODUCT_NAME,
    tagline: REPORT_TAGLINE,
    reportNumber: source.reportNumber || formatReportNumber(version),
    version: version,
    preparedAt: prepared,
    preparedLabel: formatLongDate(prepared) || formatLongDate(now.toISOString()),
    documentedSinceLabel: since ? 'Documented with Pacarion since ' + since : '',
    addressLabel: place,
    preparedBy: preparedBy,
    coverLine: COVER_LINE,
    stateLine: STATE_LINES[state] || '',
    sponsor: sponsorCard(source.sponsor),
    glance: [],
    summary: [],
    omission: leftOut,
    sections: [],
    photos: includedPhotos,
    about: [],
    toggles: toggles,
    footer: reportFooterFull(leftOut.omitted),
  };
  const projectRows = Array.isArray(source.projects) ? source.projects : [];
  snapshot.glance = glanceRows(client);
  snapshot.summary = summaryTotals(toggles, {
    maintenance: maintenance.length,
    documents: includedDocs.length,
    projects: projectRows.length,
    vendors: vendorTotal(projectRows),
  });
  const blocks = [];
  if (sectionOn(toggles, 'systems')) blocks.push(sectionBlock('systems', 'Major systems', systems, ''));
  if (sectionOn(toggles, 'maintenance')) {
    blocks.push(sectionBlock('maintenance', 'Maintenance history', maintenance, ''));
  }
  if (sectionOn(toggles, 'warranties')) blocks.push(sectionBlock('warranties', 'Warranties', warranties, ''));
  if (sectionOn(toggles, 'florida')) {
    blocks.push(sectionBlock('florida', 'Florida insurance items', floridaItemsList, FLORIDA_CAPTION));
  }
  if (sectionOn(toggles, 'documents')) blocks.push(sectionBlock('documents', 'Documents', includedDocs, ''));
  if (sectionOn(toggles, 'photos')) {
    blocks.push(sectionBlock('photos', 'Photos', includedPhotos.map((photo) => ({
      id: photo.id,
      title: photo.label,
      detail: '',
      label: LABEL_OWNER,
      note: '',
    })), ''));
  }
  snapshot.sections = blocks;
  snapshot.about = aboutParagraphs(snapshot);
  return snapshot;
}

function linkTargets(client, systemRows, now) {
  const toggles = defaultToggles(floridaHome(client));
  toggles.sections.systems = true;
  const snap = buildReportSnapshot({
    client: client,
    systems: systemRows,
    toggles: toggles,
    now: now,
    documents: [],
    tasks: [],
    photos: [],
  });
  let items = [];
  (snap.sections || []).forEach((section) => {
    if (section.id === 'systems') items = section.items || [];
  });
  return items.map((item) => ({ id: item.id, title: item.title }));
}

function catalogDocuments(documents) {
  return (documents || []).filter(Boolean).map((doc) => ({
    id: doc.id,
    title: clean(doc.title || doc.file_name) || 'Document',
    category: clean(doc.category),
    dateLabel: formatLongDate(docDate(doc)),
    kind: docKind(doc),
  }));
}

function catalogPhotos(photos) {
  return (photos || []).filter((photo) => photo && photo.id).map((photo) => ({
    id: String(photo.id),
    label: clean(photo.label) || 'Photo',
  }));
}

function hasInsuranceForm(documents) {
  const rows = documents || [];
  for (let index = 0; index < rows.length; index += 1) {
    const kind = docKind(rows[index]);
    if (kind === 'wind' || kind === 'four') return true;
  }
  return false;
}

const PRIVATE_KEYS = {
  storagePath: true,
  file_path: true,
  filePath: true,
  gps: true,
  gpsLatitude: true,
  gpsLongitude: true,
  exif: true,
  email: true,
  phone: true,
  invite_code: true,
  password_hash: true,
  notes: true,
  homeKey: true,
  home_key: true,
};

function publicItem(item, sectionId) {
  if (!item) return null;
  const detail = item.detail || '';
  return {
    id: item.id || '',
    title: item.title || '',
    detail: sectionId === 'maintenance' && !reportKeepsDueTiming(item) ? stripDueTiming(detail) : detail,
    label: item.label || '',
    note: item.note || '',
    group: item.group || '',
    when: item.when || '',
    deadline: item.deadline === true,
  };
}

function publicSnapshot(snapshot) {
  if (!snapshot || typeof snapshot !== 'object') return null;
  const sponsor = snapshot.sponsor || {};
  const sections = Array.isArray(snapshot.sections) ? snapshot.sections : [];
  return {
    schema: snapshot.schema,
    productName: snapshot.productName,
    tagline: snapshot.tagline,
    reportNumber: snapshot.reportNumber,
    version: snapshot.version,
    preparedAt: snapshot.preparedAt,
    preparedLabel: snapshot.preparedLabel,
    documentedSinceLabel: snapshot.documentedSinceLabel,
    addressLabel: snapshot.addressLabel,
    preparedBy: snapshot.preparedBy,
    coverLine: snapshot.coverLine,
    stateLine: snapshot.stateLine,
    sponsor: {
      kind: sponsor.kind,
      wordmark: sponsor.wordmark || '',
      name: sponsor.name || '',
      brokerage: sponsor.brokerage || '',
      phone: sponsor.phone || '',
      email: sponsor.email || '',
      website: sponsor.website || '',
      teamName: sponsor.teamName || '',
      logoUrl: sponsor.logoUrl || '',
      headshotUrl: sponsor.headshotUrl || '',
      brandColor: sponsor.brandColor || PACARION_BRAND_COLOR,
      line: sponsor.line || '',
    },
    glance: snapshot.glance || [],
    summary: snapshot.summary || [],
    omission: snapshot.omission || {},
    sections: sections.map((section) => ({
      id: section.id,
      title: section.title,
      caption: section.caption || '',
      empty: section.empty || '',
      items: (section.items || []).map((item) => publicItem(item, section.id)).filter(Boolean),
    })),
    photos: (snapshot.photos || []).map((photo) => ({ id: photo.id, label: photo.label })),
    about: snapshot.about || [],
    footer: snapshot.footer || '',
  };
}

module.exports = {
  PRODUCT_NAME,
  REPORT_TAGLINE,
  TEXT_VERSION,
  CONSENT_TYPE,
  SCHEMA_VERSION,
  LABEL_OWNER,
  LABEL_ATTACHED,
  LABEL_ON_FILE,
  COVER_LINE,
  EMPTY_SECTION,
  TOGGLE_HELP,
  MAINTENANCE_HELP,
  DOCUMENT_HELP,
  INSURANCE_FORM_WARNING,
  FLOOD_NOTE_WARNING,
  CLAIM_NOTE,
  DOWNLOAD_NOTE,
  VIEWER_BANNER_LEAD,
  SHARE_DAY_CHOICES,
  DEFAULT_SHARE_DAYS,
  REPORT_TERMS_PATH,
  INTERSTITIAL_TITLE,
  INTERSTITIAL_BODY,
  INTERSTITIAL_BUTTON,
  INTERSTITIAL_TERMS,
  SHARE_CONSENT_BODY,
  SHARE_CONSENT_CHECKBOX,
  CREATE_BUTTON,
  FLORIDA_CAPTION,
  VIEWER_TERMS,
  SECTIONS,
  PRIVATE_KEYS,
  floridaHome,
  availableSections,
  normalizeToggles,
  defaultToggles,
  omission,
  formatReportNumber,
  shareDays,
  shareIsOpen,
  reportShareFromPath,
  isReportTermsPath,
  reportPath,
  requestMessage,
  shareConsentNotice,
  formDated,
  requestCardOpen,
  reportRequestDecision,
  agentReportStatus,
  requestCanTakeShare,
  validShareId,
  preparedByLine,
  buildReportSnapshot,
  publicSnapshot,
  FINANCIAL_CATEGORIES,
  FINANCIAL_DEADLINE_TITLES,
  reportKeepsDueTiming,
  linkTargets,
  catalogDocuments,
  catalogPhotos,
  notesMentionFlood,
  hasInsuranceForm,
  formatLongDate,
  addressLabel,
};
